import { useMapContext } from '@rio-cloud/rio-uikit/MapContext';
import { useEffect } from 'react';

import type { MapApi, Position } from './types';

const POSITION_EPSILON = 1e-6;

type MapUtils = NonNullable<MapApi['utils']>;

interface CenterMapOnPositionProps {
    center?: Position;
    /**
     * Animate the move instead of jumping. Only short moves are ever animated — see
     * `canAnimateTo` for why.
     */
    animate?: boolean;
}

/**
 * Is the target still inside what the map is showing right now?
 *
 * This decides whether animating is a good idea. UIKit's `setCenter` is not a plain pan: it goes
 * through HERE's `setLookAtData(..., animate)`, which flies the camera to the target and zooms out
 * on the way there and back in at the end. The further it has to travel, the deeper that dip goes.
 *
 * Measured from a zoom of 19: a move inside the viewport does not dip at all, three viewports dips
 * to ~17, and jumping to a vehicle ~55 km away collapses to ~10 and takes about eight seconds to
 * climb back. Anything touching the camera in the meantime (the mission route being revealed, the
 * next position update) cuts the flight short and leaves the map zoomed out.
 *
 * So: animate the small corrections that come from following a moving vehicle, and jump for
 * everything else — which in practice means switching to a different vehicle.
 */
const canAnimateTo = (utils: MapUtils, center: Position) => {
    const { topLeft, bottomRight } = utils.getBounds();
    const north = topLeft.lat;
    const south = bottomRight.lat;
    const west = topLeft.lng;
    const east = bottomRight.lng;

    if (north === undefined || south === undefined || west === undefined || east === undefined) {
        return false;
    }

    return center.lat <= north && center.lat >= south && center.lng >= west && center.lng <= east;
};

const CenterMapOnPosition = ({ center, animate = false }: CenterMapOnPositionProps) => {
    const { api } = useMapContext();
    const utils = api.utils;

    useEffect(() => {
        if (!center || !utils) {
            return;
        }

        const current = utils.getCenter();
        const isAlreadyCentred =
            Math.abs(current.lat - center.lat) < POSITION_EPSILON &&
            Math.abs(current.lng - center.lng) < POSITION_EPSILON;

        if (isAlreadyCentred) {
            return;
        }

        utils.setCenter(center, animate && canAnimateTo(utils, center));
    }, [utils, center, animate]);

    return null;
};

export default CenterMapOnPosition;
